import { CutToSizeGroupModel } from '../CutToSizeGroup/types/models'
import { BasketItemModel, CrossSellConfiguration, CrossSellProvider, CrossSellRequirement, CrossSellRule, ProductModel, SuitableSizesForTubes } from './types/models'

export type CrossSellResult = {
  quantity: number;
  product: ProductModel;
  cross_sell_configuration: CrossSellConfiguration|null;
  cut_to_size_rep: CutToSizeGroupModel
  dimension: string
  thickness: string
}

type CrossSellRequirementWithSatisfaction = CrossSellRequirement & {
  satisfied: number;
}

type CrossSellSupply = CrossSellRequirement

function isAdhesive (productName: string): boolean {
  return productName === 'Pinkgrip Adhesive - 350ml'
}

function isApplicatorGun (productName: string): boolean {
  return productName === 'Applicator Gun - 270ml - 425ml'
}

function isFixingKit (productName: string): boolean {
  return /fixing kit/i.test(productName)
}

function isMeshPanelClip (productName: string): boolean {
  return /(Single|Double) Sided Mesh Panel Clip/.test(productName)
}

function isFloorGratingClip (productName: string): boolean {
  return productName === 'Floor Grating Clip - 310 (price is per clip)'
}

function isSuitableMeshPanelClipSize (productName: string, suitableSizesForTubes: number[]): boolean {
  return suitableSizesForTubes.some((tubeSize) => productName.includes(tubeSize.toString()))
}

function isSheet (basketItem: BasketItemModel): boolean {
  return basketItem.is_a_sheet
}

function isCutToSize (productName: string): boolean {
  return /Cut to Size/.test(productName)
}

function matchingTubeAndClamp (basketItems: BasketItemModel[]): boolean {
  const tubesSizes = basketItems.filter((item) => item.is_tube).map((item) => item.size)
  const clampSizes = basketItems.filter(item => item.is_clamp).map((item) => item.size)
  let result = false
  tubesSizes.forEach((tubeSize) => {
    if (clampSizes.includes(tubeSize)) { result = true }
  })
  return result
}

const CrossSell = (crossSellProviders: CrossSellProvider[], crossSellRules: CrossSellRule[], basketItems: BasketItemModel[], suitableSizesForTubes: SuitableSizesForTubes): CrossSellResult[] => {
  const requirementOptions: CrossSellRequirementWithSatisfaction[][] = []
  const rulesWithRequirements = crossSellRules.filter((rule) => rule.requirements.length > 0)

  const hasMatchingTubeClamps = matchingTubeAndClamp(basketItems)

  basketItems.forEach((item) => {
    const applicableRules = rulesWithRequirements.filter((rule) => typeof rule.sku_including !== 'undefined' && rule.sku_including.includes(item.product.sku))
    applicableRules.forEach((rule) => {
      const requirements: CrossSellRequirementWithSatisfaction[] = []
      rule.requirements.forEach((requirementOptionSet) => {
        for (let i = 0; i < requirementOptionSet.length; i++) {
          const requirement = requirementOptionSet[i]
          let quantity = requirement.quantity * item.quantity
          if (isMeshPanelClip(requirement.item) && !isSuitableMeshPanelClipSize(requirement.item, suitableSizesForTubes)) {
            continue
          }
          if (hasMatchingTubeClamps && isCutToSize(requirement.item)) {
            continue
          }
          if (item.number_of_holes > 0) {
            if (isAdhesive(requirement.item) || isApplicatorGun(requirement.item)) continue
          }
          if (isSheet(item) && isFixingKit(requirement.item)) {
            if (item.number_of_holes === 0) {
              continue
            } else {
              quantity = item.quantity * requirement.quantity * item.number_of_holes
            }
          }
          requirements.push({ item: requirement.item, quantity, satisfied: 0 })
        }
      })
      requirementOptions.push(requirements)
    })
  })

  // First get supplies
  const supplies: CrossSellSupply[] = []
  basketItems.forEach((item) => {
    const provider = crossSellProviders.find((provider) => provider.products.find((product) => product.sku === item.product.sku))
    if (provider) {
      const providedItem = provider.products.find((product) => product.sku === item.product.sku)
      if (providedItem) {
        supplies.push({ item: provider.provides, quantity: item.quantity * providedItem.quantity })
      }
    }
  })
  // Merge supplies with same item
  const mergedSupplies: CrossSellSupply[] = []
  supplies.forEach((supply) => {
    const existingSupply = mergedSupplies.find((existing) => existing.item === supply.item)
    if (existingSupply) {
      existingSupply.quantity += supply.quantity
    } else {
      mergedSupplies.push(supply)
    }
  })

  // Satisfy requirements from basket
  requirementOptions.forEach((requirements) => {
    // Use for to allow breaking
    for (let i = 0; i < requirements.length; i++) {
      const requirement = requirements[i]
      const supply = mergedSupplies.find((supply) => supply.item === requirement.item)
      if (supply) {
        if (supply.quantity >= requirement.quantity) {
          requirement.satisfied = requirement.quantity
          supply.quantity -= requirement.quantity
          break
        } else {
          requirement.satisfied = supply.quantity
          supply.quantity = 0
        }
      }
    }
  })

  // Get unsatisfied requirements
  const unsatisfiedRequirements: CrossSellRequirementWithSatisfaction[] = []
  requirementOptions.forEach((requirements) => {
    if (requirements.every((requirement) => requirement.satisfied !== requirement.quantity)) {
      let subsequentUnsatisfied = false
      requirements.forEach((requirement) => {
        const unsatisfiedRequirement = unsatisfiedRequirements.find((existing) => existing.item === requirement.item)
        let quantityAdjustment = subsequentUnsatisfied ? 0 : (requirement.quantity - requirement.satisfied)
        if (isApplicatorGun(requirement.item)) {
          quantityAdjustment = 1
        }
        if (unsatisfiedRequirement) {
          unsatisfiedRequirement.quantity += quantityAdjustment
        } else {
          unsatisfiedRequirements.push({ item: requirement.item, quantity: quantityAdjustment, satisfied: 0 })
        }
        subsequentUnsatisfied = true
      })
    }
  })

  // Convert unsatisfied requirements to cross-sell products, with quantity 0 suggesting the first available
  const crossSellResults: CrossSellResult[] = []
  unsatisfiedRequirements.forEach((requirement) => {
    const provider = crossSellProviders.find((provider) => provider.provides === requirement.item)
    if (!provider) {
      return
    }
    if (requirement.quantity === 0) {
      crossSellResults.push({
        quantity: 0,
        product: provider.products[0].product,
        cross_sell_configuration: provider.products[0].cross_sell_configuration,
        cut_to_size_rep: provider.products[0].cut_to_size_rep,
        thickness: provider.products[0].thickness,
        dimension: provider.products[0].dimension
      })
    } else {
      while (requirement.quantity > 0) {
        const product = [...provider.products].reverse().find((product) => product.quantity <= requirement.quantity) || provider.products[0]
        const quantity = (product === provider.products[0]) ? Math.ceil(requirement.quantity / product.quantity) : Math.max(1, Math.floor(requirement.quantity / product.quantity))
        crossSellResults.push({
          quantity,
          product: product.product,
          cross_sell_configuration: product.cross_sell_configuration,
          cut_to_size_rep: product.cut_to_size_rep,
          thickness: product.thickness,
          dimension: product.dimension
        })
        requirement.quantity -= quantity * product.quantity
        if (isAdhesive(product.product.name)) {
          crossSellResults[crossSellResults.length - 1].quantity = 1
        }
        if (isFloorGratingClip(product.product.name) || isMeshPanelClip(product.product.name)) {
          crossSellResults[crossSellResults.length - 1].quantity = 0
        }
      }

      // If the provider contains a product that is marked as an optional suggestion that is not already part of the cross sell results
      // add it with a quantity of 0 so the user can choose which varient of the product they want (e.g. a box of 100 might be more cost
      // effective against a pack of 4)
      const optionalSuggestions = provider.products.filter((product) => product.optional_suggestion)
      optionalSuggestions.forEach((optionalSuggestion) => {
        if (!crossSellResults.some((result) => result.product.id === optionalSuggestion.product.id)) {
          crossSellResults.push({
            quantity: 0,
            product: optionalSuggestion.product,
            cross_sell_configuration: optionalSuggestion.cross_sell_configuration,
            cut_to_size_rep: optionalSuggestion.cut_to_size_rep,
            thickness: optionalSuggestion.thickness,
            dimension: optionalSuggestion.dimension
          })
        }
      })
    }
  })

  return crossSellResults
}

export default CrossSell
